import React, { useState, useEffect, useRef } from 'react';
import { Trash2, ExternalLink, Edit, Save, X } from 'lucide-react';
import {
    fetchDealerLocationLinks,
    createDealerLocationLink,
    updateDealerLocationLink,
    deleteDealerLocationLink
} from '../services/apiService';
import { apiClient } from '../config/api';

const DealerLocationLinksManager = React.forwardRef(({ locationId, linksTemplate }, ref) => {
    const [locationLinks, setLocationLinks] = useState({ sales: [], service: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allLinks, setAllLinks] = useState({ sales: [], service: [] });
    const [editingLinks, setEditingLinks] = useState({}); // Track which links are being edited
    const [editedLinks, setEditedLinks] = useState(new Set()); // Track which links have been edited
    const [validationErrors, setValidationErrors] = useState([]);
    const [showExitModal, setShowExitModal] = useState(false);

    useEffect(() => {
        if (locationId) {
            loadLocationLinks();
        }
    }, [locationId]);

    const loadLocationLinks = async () => {
        try {
            setLoading(true);
            const [links, adminLinks] = await Promise.all([
                fetchDealerLocationLinks(locationId),
                apiClient.get('/admin/links') // Get admin links config
            ]);
            setLocationLinks(links);

            // Transform admin links to match expected structure
            const transformedLinks = {
                sales: adminLinks.filter(link => link.link_type === 'sales'),
                service: adminLinks.filter(link => link.link_type === 'service')
            };
            setAllLinks(transformedLinks);
        } catch (error) {
            console.error('Error loading location links:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditLink = (linkType, linkKey) => {
        const editKey = `${linkType}_${linkKey}`;
        const existingLink = locationLinks[linkType]?.find(link => link.link_key === linkKey);

        setEditingLinks(prev => ({
            ...prev,
            [editKey]: existingLink?.url || ''
        }));
    };

    const handleSaveLink = async (linkType, linkKey) => {
        const editKey = `${linkType}_${linkKey}`;
        const newUrl = (editingLinks[editKey] || '').trim();
        const templateLink = allLinks[linkType]?.find(link => link.link_key === linkKey);

        // Validate URL if not empty
        if (newUrl !== '' && !isValidUrl(newUrl)) {
            alert(`Please enter a valid URL starting with http:// or https://\n\nExample: https://www.example.com`);
            return;
        }

        // Check if required field is empty
        if (templateLink?.is_required && newUrl === '') {
            alert(`${templateLink.link_label} is required and cannot be left empty.`);
            return;
        }

        try {
            setSaving(true);

            const existingLink = locationLinks[linkType]?.find(link => link.link_key === linkKey);

            if (newUrl === '') {
                // Delete link if URL is empty and link exists
                if (existingLink?.link_instance_id) {
                    await deleteDealerLocationLink(locationId, existingLink.link_instance_id);
                }
            } else {
                if (existingLink?.link_instance_id) {
                    // Update existing link
                    await updateDealerLocationLink(locationId, existingLink.link_instance_id, newUrl);
                } else {
                    // Create new link
                    await createDealerLocationLink(locationId, templateLink.id, newUrl);
                }
            }

            // Mark as edited and reload data
            setEditedLinks(prev => new Set([...prev, editKey]));
            await loadLocationLinks();

            // Exit edit mode
            setEditingLinks(prev => {
                const next = { ...prev };
                delete next[editKey];
                return next;
            });
        } catch (error) {
            console.error('Error updating link:', error);
            alert('Failed to update link. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = (linkType, linkKey) => {
        const editKey = `${linkType}_${linkKey}`;
        setEditingLinks(prev => {
            const next = { ...prev };
            delete next[editKey];
            return next;
        });
    };

    const handleDeleteLink = async (linkType, linkInstanceId) => {
        if (!window.confirm('Are you sure you want to delete this link?')) return;

        try {
            setSaving(true);
            await deleteDealerLocationLink(locationId, linkInstanceId);
            await loadLocationLinks();
        } catch (error) {
            console.error('Error deleting link:', error);
            alert('Failed to delete link. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // URL validation helper with regex
    const isValidUrl = (string) => {
        if (!string || string.trim() === '') return false;

        // Basic URL regex that matches http/https URLs
        const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

        // Also check with URL constructor as fallback
        try {
            const url = new URL(string);
            return urlRegex.test(string) && (url.protocol === 'http:' || url.protocol === 'https:');
        } catch (_) {
            return false;
        }
    };

    // Validation function to check only edited required fields
    const validateRequiredFields = () => {
        const errors = [];

        ['sales', 'service'].forEach(type => {
            const templateLinks = allLinks[type] || [];

            templateLinks.forEach(templateLink => {
                const editKey = `${type}_${templateLink.link_key}`;
                const existingLink = locationLinks[type]?.find(link => link.link_key === templateLink.link_key);
                const currentValue = existingLink?.url || '';

                // Only validate if this link has been edited and is required
                if (editedLinks.has(editKey) && templateLink.is_required) {
                    if (!currentValue || currentValue.trim() === '') {
                        errors.push(`${templateLink.link_label} (${type}) is required`);
                    }
                }

                // Check URL validity for any non-empty edited field
                if (editedLinks.has(editKey) && currentValue && currentValue.trim() !== '') {
                    if (!isValidUrl(currentValue.trim())) {
                        errors.push(`${templateLink.link_label} (${type}) is not a valid URL`);
                    }
                }
            });
        });

        setValidationErrors(errors);

        // Show modal if there are errors
        if (errors.length > 0) {
            setShowExitModal(true);
            return false;
        }

        return true;
    };

    // Expose validation function to parent component
    React.useImperativeHandle(ref, () => ({
        validateRequiredFields
    }));

    const renderLinksSection = (type) => {
        const templateLinks = allLinks[type] || [];
        const existingLinks = locationLinks[type] || [];

        if (templateLinks.length === 0) {
            return (
                <div key={type} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-lg font-semibold mb-3 capitalize">{type} Links</h4>
                    <p className="text-gray-500 text-sm">No {type} link types configured by admin.</p>
                </div>
            );
        }

        return (
            <div key={type} className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-3 capitalize">{type} Links</h4>
                <div className="space-y-3">
                    {templateLinks.map(templateLink => {
                        // Find existing link data
                        const existingLink = existingLinks.find(
                            link => link.link_key === templateLink.link_key
                        );

                        const editKey = `${type}_${templateLink.link_key}`;
                        const isEditing = editKey in editingLinks;
                        const hasBeenEdited = editedLinks.has(editKey);
                        const currentValue = existingLink?.url || '';

                        return (
                            <div key={templateLink.id} className="flex items-center gap-2">
                                <label className="w-32 text-sm font-medium text-gray-700">
                                    {templateLink.link_label}
                                    {templateLink.is_required && <span className="text-red-500 ml-1">*</span>}
                                    {hasBeenEdited && <span className="text-green-500 ml-1">✓</span>}:
                                </label>
                                <div className="flex-1 flex items-center gap-2">
                                    {isEditing ? (
                                        // Edit mode
                                        <>
                                            <input
                                                type="url"
                                                value={editingLinks[editKey]}
                                                onChange={(e) => setEditingLinks(prev => ({
                                                    ...prev,
                                                    [editKey]: e.target.value
                                                }))}
                                                placeholder={`Enter ${templateLink.link_label.toLowerCase()} URL${templateLink.is_required ? ' (required)' : ''}`}
                                                className="flex-1 p-2 border rounded-md text-sm border-blue-300"
                                                disabled={saving}
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => handleSaveLink(type, templateLink.link_key)}
                                                className="p-2 text-green-600 hover:text-green-800"
                                                title="Save changes"
                                                disabled={saving}
                                            >
                                                <Save size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleCancelEdit(type, templateLink.link_key)}
                                                className="p-2 text-gray-600 hover:text-gray-800"
                                                title="Cancel edit"
                                                disabled={saving}
                                            >
                                                <X size={16} />
                                            </button>
                                        </>
                                    ) : (
                                        // Display mode
                                        <>
                                            <div className={`flex-1 p-2 border rounded-md text-sm bg-gray-50 ${
                                                templateLink.is_required && !currentValue && hasBeenEdited
                                                    ? 'border-red-300 text-red-700'
                                                    : 'border-gray-300 text-gray-700'
                                            }`}>
                                                {currentValue || `Enter ${templateLink.link_label.toLowerCase()} URL${templateLink.is_required ? ' (required)' : ''}`}
                                            </div>
                                            <button
                                                onClick={() => handleEditLink(type, templateLink.link_key)}
                                                className="p-2 text-gray-500 hover:text-gray-700"
                                                title="Edit link"
                                                disabled={saving}
                                            >
                                                <Edit size={16} />
                                            </button>
                                            {currentValue && (
                                                <>
                                                    <a
                                                        href={currentValue}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 text-blue-600 hover:text-blue-800"
                                                        title="Open link"
                                                    >
                                                        <ExternalLink size={16} />
                                                    </a>
                                                    <button
                                                        onClick={() => handleDeleteLink(type, existingLink?.link_instance_id)}
                                                        className="p-2 text-red-600 hover:text-red-800"
                                                        title="Delete link"
                                                        disabled={saving}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-32">
                <div className="text-gray-500">Loading links...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Location Links</h3>
                {saving && (
                    <div className="text-sm text-blue-600">Saving changes...</div>
                )}
            </div>


            <div className="space-y-4">
                {renderLinksSection('sales')}
                {renderLinksSection('service')}
            </div>

            <div className="text-xs text-gray-500 mt-4">
                Click the edit icon to modify links. URLs must start with http:// or https://
                <br />
                <span className="text-red-500">*</span> Required fields must be completed before leaving the page.
                <span className="text-green-500 ml-2">✓</span> Indicates edited fields.
            </div>

            {/* Exit Warning Modal */}
            {showExitModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-4 text-red-600">Incomplete Required Fields</h3>
                        <p className="text-gray-700 mb-4">
                            You have unfinished required fields or invalid URLs that need to be completed:
                        </p>
                        <ul className="list-disc pl-6 mb-6 space-y-1">
                            {validationErrors.map((error, index) => (
                                <li key={index} className="text-sm text-red-600">{error}</li>
                            ))}
                        </ul>
                        <p className="text-gray-700 mb-6">
                            Are you sure you want to leave this page without completing these fields?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowExitModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Stay and Complete
                            </button>
                            <button
                                onClick={() => {
                                    setShowExitModal(false);
                                    setValidationErrors([]);
                                    // Allow navigation by returning true
                                    window.history.back();
                                }}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                                Leave Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default DealerLocationLinksManager;